import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import URLs from '../../../../../utils/appURLs';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';
import axiosInstance from '../../../../../app/config/axiosInstance';

const serviceContext = process.env.REACT_APP_SERVICE_CONTEXT;

type ReturnedType = any; // The type of the return of the thunk
type ThunkArg = {
  myLocationName: string;
  latitude: number;
  longitude: number;
  landMarkName: string;
  landMarkLatitude: number;
  landMarkLongitude: number;
};

type LandMarkList = {
  landMarkName: string;
  landMarkLatitude: number;
  landMarkLongitude: number;
};

type addThunkArg = {
  myLocationName: string;
  latitude: number;
  longitude: number;
  landMarkList: LandMarkList[];
};

type updateThunkArg = {
  payload: addThunkArg;
  mylocationid: number;
};

type deleteThunkArg = {
  category: string;
  mylocationid: number;
  landmarkids: number;
};

type locationResponse = {
  userID: number;
  location: string;
  myLocationName: string;
  latitude: number;
  longitude: number;
  isPinned: number;
  isActive: number;
  createdAt: number;
  modifiedAt: number;
  createdBy: number;
  modifiedBy: number;
  landMarkName: string;
};

type initialStateProps = {
  isLoading: boolean;
  type:
    | 'locations'
    | 'add-location'
    | 'update-location'
    | 'pin-location'
    | 'delete-location';
  data: locationResponse | any;
  error: string | null;
};

const initialState: initialStateProps = {
  isLoading: false,
  type: 'locations',
  data: null,
  error: null
};

export const getmyLocation = createAsyncThunk('myLocation/get', async (_, thunkApi) => {
  try {
    const response = await axiosInstance({
      method: 'GET',
      url: `${serviceContext}${URLs.GET_MY_LOCATION}`
    });
    return response?.data?.data;
  } catch (error: any) {
    return thunkApi.rejectWithValue(error);
  }
});

// Action function to add myLocation details:
export const addMyLocation = createAsyncThunk<ReturnedType, addThunkArg>(
  'myLocation/add',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'POST',
        url: `${serviceContext}${URLs.ADD_MY_LOCATION}`,
        data
      });

      thunkApi.dispatch(
        updateToast({
          show: true,
          message: 'Location added successfully',
          severity: 'success'
        })
      );

      return response;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error.response.data?.error,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// Action function to update myLocation details:
export const updateMyLocation = createAsyncThunk<ReturnedType, updateThunkArg>(
  'myLocation/update',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'PUT',
        url: `${serviceContext}${URLs.UPDATE_MY_LOCATION}mylocationid=${data?.mylocationid}`,
        data: data?.payload
      });

      thunkApi.dispatch(
        updateToast({
          show: true,
          message: 'Location updated successfully',
          severity: 'success'
        })
      );

      return response;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error.response.data?.error,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// Action function to pin and unpin myLocation details:
export const pinnedLocation = createAsyncThunk<ReturnedType, number>(
  'myLocation/pin/unpin',
  async (id, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'PATCH',
        url: `${serviceContext}${URLs.PIN_MY_LOCATION}mylocationid=${id}`
      });
      thunkApi.dispatch(getmyLocation());
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response.data?.message,
          severity: 'success'
        })
      );

      return response;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error.response.data?.error,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// Action function to delete myLocation details:
export const deleteMyLocation = createAsyncThunk<ReturnedType, deleteThunkArg>(
  'myLocation/delete',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'DELETE',
        url: `${serviceContext}${URLs.DELETE_MY_LOCATION}mylocationid=${data}`
      });

      thunkApi.dispatch(
        updateToast({
          show: true,
          message: 'Location deleted successfully',
          severity: 'success'
        })
      );
      return response;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error.response.data?.error,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

const getmyLocationSlice = createSlice({
  name: 'myLocation',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder

      // Get myLocation details:
      .addCase(getmyLocation.pending, state => {
        state.isLoading = true;
        state.type = 'locations';
        state.data = null;
        state.error = null;
      })
      .addCase(getmyLocation.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(getmyLocation.rejected, (state, action: any) => {
        state.isLoading = false;
        state.data = [];
        state.error = action.payload.response?.data?.error;
      })

      // Add myLocation details:
      .addCase(addMyLocation.pending, state => {
        state.isLoading = true;
        state.type = 'add-location';
        state.error = null;
      })
      .addCase(addMyLocation.fulfilled, (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.data = action.payload.data;
      })
      .addCase(addMyLocation.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload.response?.data?.error;
      })

      // Update myLocation details:
      .addCase(updateMyLocation.pending, state => {
        state.isLoading = true;
        state.type = 'update-location';
        state.error = null;
      })
      .addCase(
        updateMyLocation.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.isLoading = false;
          state.data = action.payload.data;
          state.error = null;
        }
      )
      .addCase(updateMyLocation.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload.response?.data?.error;
      })

      // Delete myLocation details:
      .addCase(deleteMyLocation.pending, state => {
        state.isLoading = true;
        state.type = 'delete-location';
        state.error = null;
      })
      .addCase(
        deleteMyLocation.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.isLoading = false;
          state.data = action.payload.data;
          state.error = null;
        }
      )
      .addCase(deleteMyLocation.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload.response?.data?.error;
      })

      // Pin and unpin myLocation details:
      .addCase(pinnedLocation.pending, state => {
        state.isLoading = true;
        state.type = 'pin-location';
        state.error = null;
      })
      .addCase(pinnedLocation.fulfilled, (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(pinnedLocation.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload.response?.data?.error;
      });
  }
});

export default getmyLocationSlice.reducer;
